import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useForm, useWatch } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useFormAutoSave } from "hooks";
import type { Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { store } from "app/store";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import WordProcessorField from "components/word-processor-field";
import {
  emptyWordProcessorValue,
  isWordProcessorHtmlEqual
} from "components/word-processor-field/word-processor-value.utils";
import { setAiPromptDetails } from "components/word-processor-field/word-processor.slice";
import { PROMPT_TYPE_SCRIBE_CONTEXT } from "constants/prompts";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import ScribeCustomContextInfoBanner from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details/scribe-custom-context-info-banner.component";
import {
  patchAmbientScribeDetailCustomContext,
  useAskAiOnScribeContextMutation,
  useSyncAmbientScribeContextMutation,
  useUpdateAmbientScribeMutation
} from "services/ambient-scribe";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import { CONTEXT_SAVE_FLUSH_TIMEOUT_MS } from "./paused-scribe-actions.util";
import {
  SCRIBE_CONTEXT_CHART_NOTE_HELPER_LINES,
  SCRIBE_CONTEXT_CHART_NOTE_REFRESH_BUTTON,
  SCRIBE_CONTEXT_CHART_NOTE_REFRESH_MESSAGE,
  SCRIBE_CONTEXT_SYNC_ERROR_MESSAGE
} from "./scribe-context-copy";
import { resolveSyncContextApplyAction } from "./scribe-context-sync.util";
import { isCustomContextEmpty } from "./scribe-context.util";

type ContextForm = {
  custom_context: string;
};

type ScribeContextEditorProps = {
  scribeId: number;
  customContext?: string | null;
  hasLinkedChartNote?: boolean;
  patientId?: number;
  userId?: number;
  isActive: boolean;
  /**
   * Sync on mount rather than only on activation, so the caller's tab-label content dot is correct
   * before the tab is ever opened — the merge is what populates `custom_context` in the first place.
   *
   * Off by default because a sync is a server WRITE: a surface whose labels carry no content dot
   * (EditScribeTab) gains nothing from it, while risking a sync-error snackbar for a tab the user
   * never opened.
   */
  syncOnMount?: boolean;
  onFlushSaveRef?: MutableRefObject<(() => Promise<void>) | null>;
};

export default function ScribeContextEditor({
  scribeId,
  customContext,
  hasLinkedChartNote = false,
  patientId,
  userId,
  isActive,
  syncOnMount = false,
  onFlushSaveRef
}: Readonly<ScribeContextEditorProps>) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const clinic_id = currentClinic?.id ? Number(currentClinic.id) : undefined;

  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorInstanceRef = useRef<Editor | null>(null);
  const [editorHeight, setEditorHeight] = useState(300);
  const [showChartNoteRefreshBanner, setShowChartNoteRefreshBanner] =
    useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const syncGenerationRef = useRef(0);
  const syncInFlightRef = useRef<Promise<AmbientScribe | null> | null>(null);
  const isDirtyRef = useRef(false);
  const customContextValueRef = useRef("");
  const isUpdatingRef = useRef(false);
  const isDebouncingRef = useRef(false);
  const saveImmediatelyRef = useRef<(() => Promise<void>) | null>(null);
  const prevIsDirtyRef = useRef(false);

  const [updateScribe, { isLoading: isManualSaving }] =
    useUpdateAmbientScribeMutation();
  const [syncAmbientScribeContext] = useSyncAmbientScribeContextMutation();
  const [askAiOnContext] = useAskAiOnScribeContextMutation();

  const { control, reset, formState } = useForm<ContextForm>({
    defaultValues: { custom_context: "" }
  });

  const [currentSavedData, setCurrentSavedData] = useState<ContextForm>({
    custom_context: ""
  });

  const customContextValue = useWatch({ control, name: "custom_context" });
  const showContextBanner = isCustomContextEmpty(customContextValue);

  isDirtyRef.current = formState.isDirty;
  customContextValueRef.current = customContextValue ?? "";

  useEffect(() => {
    syncGenerationRef.current += 1;
    setShowChartNoteRefreshBanner(false);
    const values = { custom_context: customContext ?? "" };
    reset(values);
    setCurrentSavedData(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scribeId]);

  const postSave = useCallback(
    (data: ContextForm) => {
      setCurrentSavedData(data);
      reset(data);
    },
    [reset]
  );

  const skipUpdateComparator = useCallback(
    (newData: ContextForm) =>
      isWordProcessorHtmlEqual(
        currentSavedData.custom_context,
        newData.custom_context
      ),
    [currentSavedData]
  );

  const onSubmit = useCallback(
    (data: ContextForm) =>
      updateScribe({
        id: scribeId,
        custom_context: data.custom_context || emptyWordProcessorValue,
        // Detail-only cache patch; scribe list updates on explicit user actions.
        skipCacheInvalidation: true
      }),
    [scribeId, updateScribe]
  );

  const { dataSaveStateContent, isUpdating, isDebouncing, saveImmediately } =
    useFormAutoSave({
      control,
      delay: 3000,
      updateData: onSubmit,
      reset: postSave,
      skipUpdateComparator,
      groupKey: `scribe-context-${scribeId}`,
      dataDogLabel: "Scribe Context"
    });

  isUpdatingRef.current = isUpdating;
  isDebouncingRef.current = isDebouncing;
  saveImmediatelyRef.current = saveImmediately;

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const waitForSaveIdle = useCallback(
    () =>
      new Promise<void>((resolve) => {
        const isIdle = () => !isUpdatingRef.current && !isDebouncingRef.current;

        if (isIdle()) {
          resolve();
          return;
        }

        const deadline = Date.now() + CONTEXT_SAVE_FLUSH_TIMEOUT_MS;
        const intervalId = window.setInterval(() => {
          if (!mountedRef.current) {
            window.clearInterval(intervalId);
            resolve();
            return;
          }
          if (isIdle() || Date.now() >= deadline) {
            window.clearInterval(intervalId);
            resolve();
          }
        }, 50);
      }),
    []
  );

  const flushPendingEdits = useCallback(async () => {
    if (isDirtyRef.current) {
      await saveImmediatelyRef.current?.();
    }
    await waitForSaveIdle();
  }, [waitForSaveIdle]);

  const patchDetailCache = useCallback(
    (data: Pick<AmbientScribe, "custom_context" | "updated_at">) => {
      patchAmbientScribeDetailCustomContext(
        dispatch,
        store.getState,
        scribeId,
        {
          custom_context: data.custom_context,
          updated_at: data.updated_at
        }
      );
    },
    [dispatch, scribeId]
  );

  const showSyncError = useCallback(() => {
    dispatch(setCustomMessage(SCRIBE_CONTEXT_SYNC_ERROR_MESSAGE));
    dispatch(setOpen(4000));
  }, [dispatch]);

  const handleSyncResult = useCallback(
    async (data: AmbientScribe, generation: number) => {
      if (generation !== syncGenerationRef.current || !mountedRef.current) {
        return;
      }

      await waitForSaveIdle();

      if (generation !== syncGenerationRef.current || !mountedRef.current) {
        return;
      }

      const syncedContext = data.custom_context ?? "";
      const action = resolveSyncContextApplyAction(
        isDirtyRef.current,
        customContextValueRef.current,
        syncedContext
      );

      if (generation !== syncGenerationRef.current || !mountedRef.current) {
        return;
      }

      if (action === "apply") {
        postSave({ custom_context: syncedContext });
        patchDetailCache(data);
        setShowChartNoteRefreshBanner(false);
      } else if (action === "pending") {
        setShowChartNoteRefreshBanner(true);
      } else {
        setShowChartNoteRefreshBanner(false);
      }
    },
    [waitForSaveIdle, postSave, patchDetailCache]
  );

  const runSync = useCallback(async () => {
    if (!scribeId) return;

    if (syncInFlightRef.current) {
      // Join the in-flight sync instead of firing a second POST — this is what keeps a mount that is
      // already active down to ONE request. Deliberately does NOT apply the result: the initiator
      // below always does that exactly once, and re-applying here both double-patched the cache and
      // re-decided apply-vs-pending against later dirty state. It also cannot be done safely, since
      // a `scribeId` change bumps `syncGenerationRef` in the reset effect above — which runs earlier
      // in the same commit — so the staleness guards would pass and write the previous scribe's
      // context onto the new one.
      await syncInFlightRef.current;
      return;
    }

    const generation = syncGenerationRef.current;
    setIsSyncing(true);

    const promise: Promise<AmbientScribe | null> = syncAmbientScribeContext({
      id: scribeId
    })
      .unwrap()
      .catch(() => {
        if (mountedRef.current && generation === syncGenerationRef.current) {
          showSyncError();
        }
        return null;
      })
      .finally(() => {
        syncInFlightRef.current = null;
        if (mountedRef.current) {
          setIsSyncing(false);
        }
      });

    syncInFlightRef.current = promise;
    const syncData = await promise;
    if (syncData) {
      await handleSyncResult(syncData, generation);
    }
  }, [scribeId, syncAmbientScribeContext, handleSyncResult, showSyncError]);

  // A chart-note-linked scribe's `custom_context` is empty server-side until this POST merges the
  // note in, so gating the merge on tab activation left the caller's "has content" dot dark until
  // the user opened the tab — exactly when the dot has stopped being useful. Syncing on mount makes
  // it correct on open. Mounting already-active costs ONE request, not two: runSync assigns
  // syncInFlightRef synchronously before its first await, and both effects run in the same commit.
  useEffect(() => {
    if (!scribeId || !syncOnMount) return;
    void runSync();
  }, [scribeId, syncOnMount, runSync]);

  // Kept alongside the mount sync: re-entering the tab is what surfaces the "New chart note updates
  // are available." banner when the form is dirty (SN-18681). Without this the banner is unreachable.
  useEffect(() => {
    if (!scribeId || !isActive) return;
    void runSync();
  }, [isActive, scribeId, runSync]);

  useEffect(() => {
    const wasDirty = prevIsDirtyRef.current;
    prevIsDirtyRef.current = formState.isDirty;

    if (!showChartNoteRefreshBanner || formState.isDirty || !wasDirty) return;
    void runSync();
  }, [formState.isDirty, showChartNoteRefreshBanner, runSync]);

  const handleRefreshChartNoteContext = useCallback(async () => {
    if (!scribeId) return;

    setIsRefreshing(true);
    try {
      await flushPendingEdits();
      const data = await syncAmbientScribeContext({ id: scribeId }).unwrap();
      if (!mountedRef.current) return;
      postSave({ custom_context: data.custom_context ?? "" });
      patchDetailCache(data);
      setShowChartNoteRefreshBanner(false);
    } catch {
      if (mountedRef.current) {
        showSyncError();
      }
    } finally {
      if (mountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [
    scribeId,
    flushPendingEdits,
    syncAmbientScribeContext,
    postSave,
    patchDetailCache,
    showSyncError
  ]);

  const saveWithCacheInvalidation = useCallback(async () => {
    if (!formState.isValid) return;

    await flushPendingEdits();

    const data: ContextForm = { custom_context: customContextValue ?? "" };
    if (skipUpdateComparator(data)) return;

    try {
      await updateScribe({
        id: scribeId,
        custom_context: data.custom_context || emptyWordProcessorValue,
        skipCacheInvalidation: false
      }).unwrap();
      postSave(data);
    } catch {
      // no invalidation — mutation invalidatesTags returns [] on error
    }
  }, [
    formState.isValid,
    flushPendingEdits,
    customContextValue,
    skipUpdateComparator,
    updateScribe,
    scribeId,
    postSave
  ]);

  useEffect(() => {
    if (onFlushSaveRef) {
      onFlushSaveRef.current = saveWithCacheInvalidation;
    }
    return () => {
      if (onFlushSaveRef) {
        onFlushSaveRef.current = null;
      }
    };
  }, [saveWithCacheInvalidation, onFlushSaveRef]);

  const resizeTinyMceEditor = useCallback((height: number) => {
    const editor = editorInstanceRef.current;
    if (!editor || editor.destroyed) return;

    const container = editor.getContainer();
    if (container) {
      container.style.height = `${height}px`;
    }
    editor.dispatch("ResizeEditor");
  }, []);

  const handleEditorAfterInit = useCallback(
    (editor: Editor) => {
      editorInstanceRef.current = editor;
      resizeTinyMceEditor(editorHeight);
    },
    [editorHeight, resizeTinyMceEditor]
  );

  useEffect(() => {
    if (!isActive) return;
    const el = editorContainerRef.current;
    if (!el) return;

    const updateHeight = () => {
      requestAnimationFrame(() => {
        const next = Math.floor(el.getBoundingClientRect().height);
        if (next > 0) {
          setEditorHeight(next);
        }
      });
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    resizeTinyMceEditor(editorHeight);
  }, [isActive, editorHeight, resizeTinyMceEditor]);

  const handleAskAi = useCallback(
    ({
      id,
      prompt,
      fieldContent,
      lastResult
    }: {
      id: string;
      prompt: string;
      fieldContent: string;
      lastResult?: string;
    }) => {
      dispatch(
        setAiPromptDetails({
          id,
          prompt,
          result: lastResult,
          dialogOpen: true,
          isBusy: true
        })
      );
      return askAiOnContext({
        scribe_id: scribeId,
        text: fieldContent,
        user_prompt: prompt,
        clinic_id
      }).then(({ data }: { data?: string }) => {
        dispatch(
          setAiPromptDetails({
            id,
            result: data,
            isBusy: false,
            dialogOpen: true
          })
        );
      });
    },
    [scribeId, clinic_id, dispatch, askAiOnContext]
  );

  const isRefreshDisabled =
    isSyncing || isRefreshing || isUpdating || isDebouncing || isManualSaving;

  return (
    <>
      {showContextBanner && <ScribeCustomContextInfoBanner userId={userId} />}
      {hasLinkedChartNote && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0.25
          }}
        >
          <Alert severity="info">
            {SCRIBE_CONTEXT_CHART_NOTE_HELPER_LINES.map((line) => (
              <Typography key={line} variant="body2" color="text.secondary">
                {line}
              </Typography>
            ))}
          </Alert>
        </Box>
      )}
      {showChartNoteRefreshBanner && (
        <Box sx={{ flexShrink: 0, px: 2, pt: 1 }}>
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={isRefreshDisabled}
                onClick={() => void handleRefreshChartNoteContext()}
              >
                {SCRIBE_CONTEXT_CHART_NOTE_REFRESH_BUTTON}
              </Button>
            }
          >
            {SCRIBE_CONTEXT_CHART_NOTE_REFRESH_MESSAGE}
          </Alert>
        </Box>
      )}
      <Box
        ref={editorContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          minWidth: 0,
          overflow: "hidden"
        }}
      >
        <WordProcessorField
          name="custom_context"
          control={control}
          placeholder="Add context that will be included in Scribe note generation…"
          mergeTagsPatientId={patientId}
          onAskAi={handleAskAi}
          askAiPromptType={PROMPT_TYPE_SCRIBE_CONTEXT}
          afterInitCallback={handleEditorAfterInit}
          height={editorHeight}
          sx={{ flex: 1, width: "100%", height: "100%" }}
          featuresGroup="light"
          allowMergeTags
          allowInsertMergeTagsValues
          nestedDialogZIndex={getScribeModalNestedDialogZIndex(
            theme.zIndex.snackbar
          )}
        />
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 0.5,
          p: 1,
          pr: 1
        }}
      >
        {dataSaveStateContent}
        <LoadingButton
          loading={isUpdating || isManualSaving}
          disabled={isUpdating || isManualSaving}
          onClick={() => saveWithCacheInvalidation()}
          variant="contained"
          size="small"
          sx={{ minWidth: "auto" }}
        >
          Save
        </LoadingButton>
      </Box>
    </>
  );
}
